import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";

import { applyBrandTokens } from "@/shared/config/brand";

export const SUPPORTED_LOCALES = [
  "en",
  "zh-Hans",
  "zh-Hant",
  "vi",
  "ja",
  "ko",
  "es",
  "fr",
  "de",
  "ar",
  "hi",
  "pt",
  "ru",
] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const RTL_LOCALES: Locale[] = ["ar"];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  "zh-Hans": "简体中文",
  "zh-Hant": "繁體中文",
  vi: "Tiếng Việt",
  ja: "日本語",
  ko: "한국어",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  ar: "العربية",
  hi: "हिन्दी",
  pt: "Português",
  ru: "Русский",
};

export const NAMESPACES = [
  "common",
  "nav",
  "market",
  "trading",
  "community",
  "advisor",
  "agent",
  "chat",
  "settings",
  "auth",
  "onboarding",
  "panels",
  "admin",
  "portfolio",
  "news",
  "dashboard",
  "verify",
  "alerts",
  "aiTasks",
] as const;

/** Languages available in the admin panel */
export const ADMIN_LOCALES = ["en", "zh-Hans", "zh-Hant", "vi"] as const;
export type AdminLocale = (typeof ADMIN_LOCALES)[number];

export const ADMIN_LOCALE_NAMES: Record<AdminLocale, string> = {
  en: "EN",
  "zh-Hans": "简",
  "zh-Hant": "繁",
  vi: "VI",
};

/**
 * Resolve raw BCP-47 tag → nearest supported locale.
 * Priority: exact match → base language match → "en"
 */
export function resolveLocale(raw: string): Locale {
  if (!raw) return "en";
  // Exact match first
  if ((SUPPORTED_LOCALES as readonly string[]).includes(raw)) return raw as Locale;
  // Chinese script variants
  if (/^zh-(tw|hk|mo|hant)/i.test(raw)) return "zh-Hant";
  if (/^zh/i.test(raw)) return "zh-Hans";
  // Base language (e.g. "vi-VN" → "vi")
  const base = raw.toLowerCase().split(/[-_]/)[0];
  const hit = (SUPPORTED_LOCALES as readonly string[]).find((l) => l === base);
  return (hit ?? "en") as Locale;
}

/**
 * Determine the locale to use on startup.
 * Source of truth (in order): xfincompass-locale key → browser language → "en"
 */
function getStartupLocale(): Locale {
  if (typeof window === "undefined") return "en";
  const saved = localStorage.getItem("xfincompass-locale");
  if (saved) {
    const resolved = resolveLocale(saved);
    // Only trust the saved value if it resolves back to itself (i.e. it's already a canonical locale)
    if (resolved === saved) return resolved;
    // saved was a raw BCP-47 tag (e.g. "vi-VN") — resolve + persist canonical form
    localStorage.setItem("xfincompass-locale", resolved);
    return resolved;
  }
  // First visit — PRIORITIZE ENGLISH (2026-06-04). We deliberately do NOT
  // auto-detect the browser locale: the product is English-first (institutional
  // research) and browser-detection landed non-English users — and the AI
  // `lang` we send — in their OS language by default. Users can still switch via
  // the language picker (persisted to xfincompass-locale → full multi-language).
  localStorage.setItem("xfincompass-locale", "en");
  return "en";
}

const startupLocale = getStartupLocale();

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Explicit language — NO LanguageDetector plugin.
    // All locale changes go through useLocale.changeLocale() which writes
    // to localStorage("xfincompass-locale") and calls i18n.changeLanguage().
    lng: startupLocale,
    fallbackLng: "en",
    supportedLngs: [...SUPPORTED_LOCALES],
    ns: [...NAMESPACES],
    // debug: true, // Uncomment to diagnose i18n issues
    defaultNS: "common",
    backend: {
      loadPath: "/locales/{{lng}}/{{ns}}.json",
      // Brand name is white-label (VITE_BRAND_NAME). Locale JSON carries
      // `{{brand}}` instead of a literal; resolve it as the bundle loads so
      // all 13 locales rebrand from env with no per-call-site plumbing.
      parse: (data: string) => applyBrandTokens(JSON.parse(data)),
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      bindI18n: "languageChanged loaded",
      bindI18nStore: "added removed",
    },
  });

/**
 * Normalize an i18n language code to the form the backend accepts
 * (4 locales: "en" | "zh-Hans" | "zh-Hant" | "vi"). Anything else falls
 * back to English. Use this when sending `lang=` query/header to BE.
 */
export function backendLang(raw: string | undefined): "en" | "zh-Hans" | "zh-Hant" | "vi" {
  const v = raw ?? "en";
  if (v === "zh-Hans" || v === "zh-Hant" || v === "en" || v === "vi") return v;
  if (/^zh-(tw|hk|mo|hant)/i.test(v)) return "zh-Hant";
  if (/^zh/i.test(v)) return "zh-Hans";
  const primary = v.toLowerCase().split(/[-_]/)[0];
  return primary === "vi" ? "vi" : "en";
}

export default i18n;
